import type { PaginatedResponse } from 'sama-shared';
import { baseApi } from 'sama-shared';

/**
 * Categorías de un aliado vistas por el superadmin de SAMA (soporte).
 * Contrato: /admin/category (SuperadminGuard). DELETE responde 409 si la
 * categoría tiene productos activos; un nombre repetido en el mismo aliado
 * también responde 409. PATCH no acepta partnerId.
 */

export interface AdminCategory {
  id: string;
  name: string;
  description: string | null;
  productsCount: number;
}

export interface GetAdminCategoriesParams {
  partnerId: string;
  page?: number;
  limit?: number;
  name?: string;
}

export const adminCategoryApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdminCategories: builder.query<PaginatedResponse<AdminCategory>, GetAdminCategoriesParams>({
      query: (params) => ({ url: '/admin/category', method: 'GET', params }),
      providesTags: (_result, _error, { partnerId }) => [{ type: 'Category', id: partnerId }],
    }),
    createAdminCategory: builder.mutation<
      AdminCategory,
      { partnerId: string; name: string; description?: string }
    >({
      query: (body) => ({ url: '/admin/category', method: 'POST', body }),
      invalidatesTags: (_result, _error, { partnerId }) => [{ type: 'Category', id: partnerId }],
    }),
    updateAdminCategory: builder.mutation<
      AdminCategory,
      { id: string; partnerId: string; name?: string; description?: string }
    >({
      // partnerId solo sirve para invalidar la lista; el backend lo rechaza en el body.
      query: ({ id, partnerId: _, ...body }) => ({
        url: `/admin/category/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { partnerId }) => [{ type: 'Category', id: partnerId }],
    }),
    deleteAdminCategory: builder.mutation<void, { id: string; partnerId: string }>({
      query: ({ id }) => ({ url: `/admin/category/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { partnerId }) => [{ type: 'Category', id: partnerId }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAdminCategoriesQuery,
  useCreateAdminCategoryMutation,
  useUpdateAdminCategoryMutation,
  useDeleteAdminCategoryMutation,
} = adminCategoryApi;
